import { apiRequest, ApiRequestError } from '../lib/httpClient'
import { asArray, parseUtcTimestamp, pickBool, pickNumber, pickString } from './shared/coercion'
import type {
  AdminSupportTicket,
  SupportTicket,
  TicketHistoryEntry,
  TicketReply,
  TicketStatus,
  TicketStatusValue,
} from '@/features/advisory-ai/assistant/types'

/**
 * Preppy support — PREPPY-SUPPORT-SPEC.md §3, extended by the API repo's
 * ADVISEPREP_SUPPORT_UI.md: a reply thread inside the product. The API emails
 * the other side a notice with a link on each message, never the message itself.
 *
 * Two surfaces: the panel every user gets, and the admin console that triages
 * and answers what they raise. Responses are camelCase and null fields are
 * omitted, so every read is defensive and null-checks use `== null` to catch
 * both a null and a missing key.
 *
 * **Admin calls take no `clientId`**: admins short-circuit the permission
 * filter, and each route finds its ticket by id. Customer calls always send it,
 * including on the thread routes addressed by ticket id, so the filter resolves
 * feature access from that argument rather than the session's default client.
 *
 * Timestamps are declared as `timestamptz` and arrive with a `Z`, unlike
 * pop-ups. They are still coerced, because the coercion is a no-op on a value
 * that already carries an offset and the alternative is every relative label in
 * the console silently skewing by the reader's offset if the column ever
 * changes.
 *
 * This file deliberately does NOT touch `support/ui/form/submit`, the existing
 * product-wide support form. That is a separate path with its own endpoint and
 * no ticket store; the two coexist by decision (spec §8.1).
 */

const BASE = 'advisoryai/support/tickets'

/** Server-side caps (spec §3). Mirrored so nobody loses a paragraph to a 400. */
export const SUPPORT_SUBJECT_MAX = 200
export const SUPPORT_AREA_MAX = 64
export const SUPPORT_BODY_MAX = 8000
export const SUPPORT_RAISED_FROM_MAX = 64
export const SUPPORT_REPLY_MAX = 8000

/**
 * The API refuses text that opens with a spreadsheet formula character, after
 * leading whitespace. That 400 is `application/problem+json`, which httpClient
 * does not parse, so the user would be shown raw JSON; checked here instead.
 */
export function startsWithFormulaCharacter(text: string): boolean {
  return /^[=+\-@]/.test(text.trimStart())
}

/** Reads a property under either casing, since the API mixes the two. */
function prop(source: unknown, name: string): unknown {
  if (!source || typeof source !== 'object') return undefined
  const record = source as Record<string, unknown>
  const pascal = name.charAt(0).toUpperCase() + name.slice(1)
  return record[name] ?? record[pascal]
}

export const TICKET_STATUSES: readonly TicketStatus[] = ['open', 'reviewing', 'awaiting', 'closed']

export function isTicketStatus(value: unknown): value is TicketStatus {
  return typeof value === 'string' && (TICKET_STATUSES as readonly string[]).includes(value)
}

/** Not coerced to a known status: opening an `open` ticket changes it, so a guess would act on the server. */
function mapStatus(value: unknown): TicketStatusValue {
  return isTicketStatus(value) ? value : 'unknown'
}

function mapTicket(payload: unknown): SupportTicket {
  const platformId = pickString(prop(payload, 'platformId'))
  const raisedFrom = pickString(prop(payload, 'raisedFrom'))
  return {
    id: pickString(prop(payload, 'ticketId')),
    reference: pickString(prop(payload, 'reference')),
    subject: pickString(prop(payload, 'subject')),
    area: pickString(prop(payload, 'area')),
    body: pickString(prop(payload, 'body')),
    status: mapStatus(prop(payload, 'status')),
    ...(platformId ? { platformId } : {}),
    ...(raisedFrom ? { raisedFrom } : {}),
    raisedAt: parseUtcTimestamp(prop(payload, 'raisedAt')),
    updatedAt: parseUtcTimestamp(prop(payload, 'updatedAt')),
  }
}

/* ── raise (spec §3.1) ────────────────────────────────────────────────────── */

export type RaiseTicketInput = {
  clientId: string
  subject: string
  area: string
  body: string
  /**
   * The connection the problem is about — NOT the advisory `orgId`, which
   * resolves against `Platform.CompanyId` and can match several rows. 400 if it
   * is another firm's.
   *
   * Omitted for a None / General ticket, which the picker offers unconditionally.
   * That depends on the server accepting a body without this key — it answered
   * 400 without it before, so this branch must not reach `dev` until that change
   * is deployed. There is no runtime flag; the gate is the release.
   */
  platformId?: string
  raisedFrom?: string
}

export async function raiseTicket(input: RaiseTicketInput): Promise<SupportTicket> {
  const raisedFrom = input.raisedFrom?.trim().slice(0, SUPPORT_RAISED_FROM_MAX)
  const payload = await apiRequest<unknown>(BASE, {
    method: 'POST',
    query: { clientId: input.clientId },
    body: {
      subject: input.subject.trim(),
      area: input.area.trim(),
      body: input.body.trim(),
      // Absence is the signal, so test for it rather than for falsiness — an empty
      // string is a caller mistake, not a request for a general ticket.
      ...(input.platformId !== undefined ? { platformId: input.platformId } : {}),
      ...(raisedFrom ? { raisedFrom } : {}),
    },
  })

  return mapTicket(payload)
}

/* ── my tickets (spec §3.2) ───────────────────────────────────────────────── */

export type MyTicketsResponse = {
  tickets: SupportTicket[]
  /** Anything not `closed` — so `reviewing` and `awaiting` count too. */
  activeCount: number
}

export async function fetchMyTickets(clientId: string): Promise<MyTicketsResponse> {
  const payload = await apiRequest<unknown>(BASE, {
    method: 'GET',
    query: { clientId },
  })

  const tickets = asArray<unknown>(prop(payload, 'tickets')).map(mapTicket)

  return {
    tickets,
    // Derived when the key is missing rather than shown as zero — the count
    // drives a badge, and a wrong zero reads as "nothing open".
    activeCount:
      prop(payload, 'activeCount') == null
        ? tickets.filter((t) => t.status !== 'closed').length
        : pickNumber(prop(payload, 'activeCount')),
  }
}

/* ── admin: all tickets (spec §3.3) ───────────────────────────────────────── */

export type AllTicketsQuery = {
  /** Not sent and not a filter — it scopes the query key and gates `enabled`. */
  clientId: string
  status?: TicketStatus | ''
  area?: string
  search?: string
  /** Narrowing to one client is this, deliberately not `clientId`. */
  clientFilter?: string
  skip?: number
  take?: number
}

export type AllTicketsResponse = {
  tickets: AdminSupportTicket[]
  /** Before paging — it is what the pager divides by. */
  totalCount: number
}

export async function fetchAllTickets(params: AllTicketsQuery): Promise<AllTicketsResponse> {
  const payload = await apiRequest<unknown>(`${BASE}/all`, {
    method: 'GET',
    query: {
      ...(params.status ? { status: params.status } : {}),
      ...(params.area ? { area: params.area } : {}),
      ...(params.search ? { search: params.search } : {}),
      ...(params.clientFilter ? { clientFilter: params.clientFilter } : {}),
      skip: params.skip ?? 0,
      take: params.take ?? 50,
    },
  })

  const tickets = asArray<unknown>(prop(payload, 'tickets')).map(
    (row): AdminSupportTicket => ({
      ...mapTicket(row),
      raisedByName: pickString(prop(row, 'raisedByName')),
      raisedByEmail: pickString(prop(row, 'raisedByEmail')),
      clientId: pickString(prop(row, 'clientId')),
      clientName: pickString(prop(row, 'clientName')),
      platformName: pickString(prop(row, 'platformName')),
    })
  )

  return {
    tickets,
    totalCount:
      prop(payload, 'totalCount') == null ? tickets.length : pickNumber(prop(payload, 'totalCount')),
  }
}

/* ── admin: change status (spec §3.4) ─────────────────────────────────────── */

/** No `clientId` — admin route, and the ticket id is the whole address (§3). */
export async function changeTicketStatus(ticketId: string, status: TicketStatus): Promise<void> {
  await apiRequest<void>(`${BASE}/${ticketId}/status`, {
    method: 'PUT',
    body: { status },
  })
}

/* ── admin: history (spec §3.6) ───────────────────────────────────────────── */

/** No `clientId` — admin route, and the ticket id is the whole address (§3). */
export async function fetchTicketHistory(ticketId: string): Promise<TicketHistoryEntry[]> {
  const payload = await apiRequest<unknown>(`${BASE}/${ticketId}/history`, {
    method: 'GET',
  })

  // Oldest first, and the opening row has no `FromStatus` at all — per the
  // null-omission rule the key is absent rather than null, so it stays
  // undefined instead of collapsing to a status the ticket was never in.
  return asArray<unknown>(payload).map((row) => {
    const from = prop(row, 'fromStatus')
    return {
      ...(from === undefined || from === null ? {} : { fromStatus: mapStatus(from) }),
      toStatus: mapStatus(prop(row, 'toStatus')),
      // A name, not an id — the endpoint joins it. This route also answers in
      // camelCase rather than the PascalCase the rest of the domain uses, which
      // `prop` absorbs; nothing downstream should care which it was.
      changedByName: pickString(prop(row, 'changedByName')),
      changedAt: parseUtcTimestamp(prop(row, 'changedAt')),
    }
  })
}

/* ── the thread: both sides; the API emails the other side a notice ───────── */

export type ReplyStatus = Extract<TicketStatus, 'reviewing' | 'awaiting' | 'closed'>

export const REPLY_STATUSES: readonly ReplyStatus[] = ['awaiting', 'reviewing', 'closed']

export function isReplyStatus(value: unknown): value is ReplyStatus {
  return typeof value === 'string' && (REPLY_STATUSES as readonly string[]).includes(value)
}

/** Ticket references as the API issues them, e.g. `DI-4821`. Email links carry one. */
export function isTicketReference(value: string): boolean {
  return /^DI-\d+$/i.test(value)
}

/**
 * `sentByEmail`, read more loosely than `pickBool` reads anything else.
 *
 * The console warns the admin when what it asked for and what the server saved
 * disagree, so a `1` collapsing to the fallback would not lose a badge — it
 * would say the customer may have been emailed when the suppression worked.
 * Narrowed to this field rather than widened in `pickBool`, because `0` there
 * can also mean "unset", which several callers deliberately default to `true`.
 */
function pickReplyFlag(value: unknown): boolean {
  if (typeof value === 'number') return Number.isFinite(value) && value !== 0
  if (typeof value === 'string') {
    const normalized = value.trim()
    if (normalized === '1') return true
    if (normalized === '0') return false
  }
  return pickBool(value)
}

function mapReply(payload: unknown): TicketReply {
  return {
    id: pickString(prop(payload, 'replyId')),
    body: pickString(prop(payload, 'body')),
    fromSupport: pickBool(prop(payload, 'fromSupport')),
    // False when absent, which is also what the customer's own messages get —
    // the flag is only ever set on support's side.
    sentByEmail: pickReplyFlag(prop(payload, 'sentByEmail')),
    sentByName: pickString(prop(payload, 'sentByName')),
    sentAt: parseUtcTimestamp(prop(payload, 'sentAt')),
  }
}

// One route serves both sides, told apart by `clientId`, so each side has its own
// function rather than an optional argument a customer call could forget.

/** Support's view: no `clientId`. */
export async function fetchTicketReplies(ticketId: string): Promise<TicketReply[]> {
  const payload = await apiRequest<unknown>(`${BASE}/${ticketId}/replies`, { method: 'GET' })
  return asArray<unknown>(payload).map(mapReply)
}

/** The raiser's view: must send `clientId`. */
export async function fetchTicketThread(ticketId: string, clientId: string): Promise<TicketReply[]> {
  const payload = await apiRequest<unknown>(`${BASE}/${ticketId}/replies`, {
    method: 'GET',
    query: { clientId },
  })
  return asArray<unknown>(payload).map(mapReply)
}

/**
 * Support's side: posts to the thread and sets the status in the same call.
 *
 * `sentByEmail` records that this text already went to the customer from an
 * admin's mail client, and suppresses the notice email the API would otherwise
 * send. Only sent when true, following the omit-optional-keys rule above.
 */
export async function sendTicketReply(
  ticketId: string,
  input: { body: string; status: ReplyStatus; sentByEmail?: boolean }
): Promise<TicketReply> {
  const payload = await apiRequest<unknown>(`${BASE}/${ticketId}/replies`, {
    method: 'POST',
    body: {
      body: input.body.trim(),
      status: input.status,
      ...(input.sentByEmail ? { sentByEmail: true } : {}),
    },
  })
  return mapReply(payload)
}

/** The customer's side. The server moves `awaiting` to `reviewing`, and refuses a closed ticket. */
export async function answerTicket(ticketId: string, clientId: string, body: string): Promise<TicketReply> {
  const payload = await apiRequest<unknown>(`${BASE}/${ticketId}/replies`, {
    method: 'POST',
    query: { clientId },
    body: { body: body.trim() },
  })
  return mapReply(payload)
}

/* ── errors ───────────────────────────────────────────────────────────────── */

/**
 * The controller answers failures with a plain-text body, and its 400s say
 * something the user can act on — the daily cap in particular.
 */
export function supportErrorMessage(error: unknown): string {
  const fallback = 'Could not send that just now. Your ticket is still here — try again in a moment.'
  if (!(error instanceof ApiRequestError)) return fallback

  const message = error.message.trim()
  // `Request failed` is httpClient's own placeholder for a body it could not
  // read, so showing it would be pretending the server said something it did not.
  if (error.status !== 400 || !message || message === 'Request failed') return fallback
  return message
}

/** A failed post saves nothing and leaves the status alone, so the same text can simply be sent again. */
export function replyErrorMessage(error: unknown): string {
  if (error instanceof ApiRequestError && error.status === 400) {
    const message = error.message.trim()
    if (message && message !== 'Request failed') return message
  }
  return 'Could not send that. Nothing was saved and your text is still here — try again in a moment.'
}
